import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAttentionBadge } from "@/contexts/AttentionContext";

// Route → title map. Order matters: mais específico primeiro.
// Dynamic patterns use includes() / startsWith() fallbacks.
// Pages that need dynamic titles (e.g. contract detail com ID) devem chamar useDocumentTitle direto.
const STATIC_MAP = {
  "/": "Marketplace B2B de creators e UGC no Brasil",
  "/login": "Entrar",
  "/register": "Criar conta",
  "/email-sent": "Confirmação enviada",
  "/terms": "Termos de Uso",
  "/privacy": "Política de Privacidade",
  "/403": "Acesso negado",
  "/dashboard": "Redirecionando",

  // Brand
  "/dashboard/brand": "Painel · Marca",
  "/dashboard/brand/discover": "Descobrir criadores",
  "/dashboard/brand/campaigns/new": "Nova campanha",
  "/dashboard/brand/contracts": "Contratos",
  "/dashboard/brand/financial": "Financeiro",

  // Creator
  "/dashboard/creator": "Painel · Criador",
  "/dashboard/creator/profile": "Meu perfil",
  "/dashboard/creator/applications": "Minhas candidaturas",
  "/dashboard/creator/invitations": "Meus convites",
  "/dashboard/creator/contracts": "Meus contratos",
  "/dashboard/creator/financial": "Financeiro",

  // Admin
  "/dashboard/admin": "Painel · Admin",
  "/dashboard/admin/operations": "Operações",
  "/dashboard/admin/brands": "Marcas",
  "/dashboard/admin/creators": "Criadores",
  "/dashboard/admin/contracts": "Contratos",
  "/dashboard/admin/audit": "Auditoria",
  "/dashboard/admin/funnel": "Funil",
  "/dashboard/admin/finance": "Financeiro",
  "/dashboard/admin/system-logs": "Logs do sistema",
  "/dashboard/admin/integrations": "Integrações",
  "/dashboard/admin/team": "Equipe",
  "/dashboard/admin/settings": "Configurações",

  "/settings/sessions": "Sessões ativas",
};

// PT-BR mirror map (Sprint Nav-2 preparação — funciona pros dois padrões)
const PT_MIRROR = {
  "/painel": "Redirecionando",
  "/cadastro": "Criar conta",
  "/termos": "Termos de Uso",
  "/como-funciona": "Como a Alta funciona",
  "/como-funciona/match": "Como o Match Score funciona",
  "/privacidade": "Política de Privacidade",
  "/email-enviado": "Confirmação enviada",
  "/painel/marca": "Painel · Marca",
  "/painel/marca/descobrir": "Descobrir criadores",
  "/painel/marca/campanhas/nova": "Nova campanha",
  "/painel/marca/contratos": "Contratos",
  "/painel/marca/financeiro": "Financeiro",
  "/painel/criador": "Painel · Criador",
  "/painel/criador/perfil": "Meu perfil",
  "/painel/criador/candidaturas": "Minhas candidaturas",
  "/painel/criador/convites": "Meus convites",
  "/painel/criador/contratos": "Meus contratos",
  "/painel/criador/financeiro": "Financeiro",
  "/painel/admin": "Painel · Admin",
  "/painel/admin/operacoes": "Operações",
  "/painel/admin/marcas": "Marcas",
  "/painel/admin/criadores": "Criadores",
  "/painel/admin/contratos": "Contratos",
  "/painel/admin/auditoria": "Auditoria",
  "/painel/admin/funil": "Funil",
  "/painel/admin/financeiro": "Financeiro",
  "/painel/admin/logs-sistema": "Logs do sistema",
  "/painel/admin/integracoes": "Integrações",
  "/painel/admin/equipe": "Equipe",
  "/painel/admin/configuracoes": "Configurações",
  "/configuracoes/sessoes": "Sessões ativas",
};

const FULL_MAP = { ...STATIC_MAP, ...PT_MIRROR };

function resolveTitle(pathname) {
  if (FULL_MAP[pathname]) return FULL_MAP[pathname];

  // Dynamic patterns
  if (pathname.startsWith("/verify/") || pathname.startsWith("/certificado/")) {
    return "Certificado de parceria";
  }
  if (pathname.startsWith("/verify-email/") || pathname.startsWith("/confirmar-email/")) {
    return "Confirmar email";
  }
  if (pathname.startsWith("/c/") || pathname.startsWith("/criador/")) {
    return "Perfil do criador";
  }
  if (pathname.startsWith("/campaigns/") || pathname.startsWith("/campanhas/")) {
    return "Briefing da campanha";
  }
  if (pathname.match(/^\/(dashboard|painel)\/(brand|marca)\/contracts\//)) return "Contrato";
  if (pathname.match(/^\/(dashboard|painel)\/(creator|criador)\/contracts\//)) return "Contrato";
  if (pathname.match(/^\/(dashboard|painel)\/(brand|marca)\/campaigns\/[^/]+\/applications$/)) return "Candidaturas da campanha";
  if (pathname.match(/^\/(dashboard|painel)\/(brand|marca)\/campanhas\/[^/]+\/candidaturas$/)) return "Candidaturas da campanha";

  return null;
}

/**
 * Watches route changes and updates document.title accordingly.
 * Prepends "(N) " when there are attention items pending (from AttentionContext).
 * Individual pages with dynamic titles can override via useDocumentTitle hook.
 */
export default function RouteTitle() {
  const location = useLocation();
  const { count } = useAttentionBadge();
  useEffect(() => {
    const brand = "Alta Creators";
    const t = resolveTitle(location.pathname);
    const base = t ? `${t} · ${brand}` : brand;
    document.title = count > 0 ? `(${count}) ${base}` : base;
  }, [location.pathname, count]);
  return null;
}
